import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useLocation } from 'react-router-dom'
import {
  FolderTree,
  ImageIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Trash2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Empty } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { CompactTableDate, commerceTableClassName } from '@/components/common/compact-table-date'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { categoriesApi } from '@/services/categories'
import type { JobCategory, JobCategoryCreatePayload } from '@/types'

const categorySchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  slug: z.string().trim().optional(),
})

type CategoryFormData = z.infer<typeof categorySchema>

const slugify = (value: string) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

function CategoryLogo({ logoUrl, name }: { logoUrl?: string | null; name: string }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className="h-9 w-9 rounded-lg border border-border/70 bg-background object-cover"
      />
    )
  }

  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-muted/40 text-muted-foreground">
      <ImageIcon className="h-4 w-4" />
    </div>
  )
}

export default function CategoriesPage() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const categoryKind = useMemo<'job' | 'project'>(() => {
    const params = new URLSearchParams(location.search)
    return params.get('kind') === 'project' ? 'project' : 'job'
  }, [location.search])
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<JobCategory | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [editCategory, setEditCategory] = useState<JobCategory | null>(null)
  const [deleteCategory, setDeleteCategory] = useState<JobCategory | null>(null)
  const createImageInputRef = useRef<HTMLInputElement | null>(null)
  const editImageInputRef = useRef<HTMLInputElement | null>(null)
  const [createLogoFile, setCreateLogoFile] = useState<File | null>(null)
  const [createLogoPreview, setCreateLogoPreview] = useState('')
  const [editLogoFile, setEditLogoFile] = useState<File | null>(null)
  const [editLogoPreview, setEditLogoPreview] = useState('')
  const [editLogoRemoved, setEditLogoRemoved] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      slug: '',
    },
  })

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    watch: watchEdit,
    formState: { errors: editErrors },
    reset: resetEdit,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      slug: '',
    },
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['job-categories', 'admin', categoryKind],
    queryFn: () => categoriesApi.getCategories({ include_inactive: true, kind: categoryKind }),
  })

  const resetCreateForm = () => {
    reset()
    setCreateLogoFile(null)
    setCreateLogoPreview('')
    if (createImageInputRef.current) createImageInputRef.current.value = ''
  }

  const resetEditForm = () => {
    resetEdit()
    setEditLogoFile(null)
    setEditLogoPreview('')
    setEditLogoRemoved(false)
    if (editImageInputRef.current) editImageInputRef.current.value = ''
  }

  const createMutation = useMutation({
    mutationFn: ({ payload, logoFile }: { payload: JobCategoryCreatePayload; logoFile?: File | null }) =>
      categoriesApi.createCategory(payload, { logoFile }),
    onSuccess: (category) => {
      toast.success('Category created successfully')
      queryClient.invalidateQueries({ queryKey: ['job-categories'] })
      setSelectedCategory(category)
      setCreateOpen(false)
      resetCreateForm()
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload, logoFile, removeLogo }: {
      id: string
      payload: CategoryFormData
      logoFile?: File | null
      removeLogo?: boolean
    }) =>
      categoriesApi.updateCategory(
        id,
        {
          name: payload.name.trim(),
          slug: payload.slug?.trim() || slugify(payload.name),
        },
        { logoFile, removeLogo },
      ),
    onSuccess: (category) => {
      toast.success('Category updated successfully')
      queryClient.invalidateQueries({ queryKey: ['job-categories'] })
      setEditCategory(null)
      resetEditForm()
      setSelectedCategory((current) => (current?.id === category.id ? category : current))
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: categoriesApi.deactivateCategory,
    onSuccess: (category) => {
      toast.success('Category deactivated successfully')
      queryClient.invalidateQueries({ queryKey: ['job-categories'] })
      setSelectedCategory((current) => (current?.id === category.id ? category : current))
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const activateMutation = useMutation({
    mutationFn: categoriesApi.activateCategory,
    onSuccess: (category) => {
      toast.success('Category activated successfully')
      queryClient.invalidateQueries({ queryKey: ['job-categories'] })
      setSelectedCategory((current) => (current?.id === category.id ? category : current))
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: categoriesApi.deleteCategory,
    onSuccess: (_result, deletedId) => {
      toast.success('Category deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['job-categories'] })
      setDeleteCategory(null)
      setSelectedCategory((current) => (current?.id === deletedId ? null : current))
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const nameValue = watch('name')
  const editNameValue = watchEdit('name')

  useEffect(() => {
    if (!editCategory) {
      return
    }

    resetEdit({
      name: editCategory.name,
      slug: editCategory.slug,
    })
    setEditLogoFile(null)
    setEditLogoPreview(editCategory.logo_url || '')
    setEditLogoRemoved(false)
    if (editImageInputRef.current) editImageInputRef.current.value = ''
  }, [editCategory, resetEdit])

  const categories = data?.results ?? []
  const filteredCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) {
      return categories
    }

    return categories.filter((category) =>
      [category.name, category.slug, category.logo_url ? 'image uploaded' : 'no image']
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch))
    )
  }, [categories, search])

  const setCreateImage = (file?: File) => {
    setCreateLogoFile(file ?? null)
    setCreateLogoPreview(file ? URL.createObjectURL(file) : '')
  }

  const setEditImage = (file?: File) => {
    setEditLogoFile(file ?? null)
    setEditLogoPreview(file ? URL.createObjectURL(file) : '')
    setEditLogoRemoved(false)
  }

  const onSubmit = (formData: CategoryFormData) => {
    createMutation.mutate({
      payload: {
        name: formData.name.trim(),
        slug: formData.slug?.trim() || slugify(formData.name),
      },
      logoFile: createLogoFile,
    })
  }

  const onEditSubmit = (formData: CategoryFormData) => {
    if (!editCategory) {
      return
    }

    updateMutation.mutate({
      id: String(editCategory.id),
      payload: formData,
      logoFile: editLogoFile,
      removeLogo: editLogoRemoved,
    })
  }

  const openEditDialog = (category: JobCategory) => {
    setEditCategory(category)
  }

  const toggleCategoryActive = (category: JobCategory) => {
    if (category.active === false) {
      activateMutation.mutate(String(category.id))
      return
    }

    deactivateMutation.mutate(String(category.id))
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-destructive/20 bg-card">
        <div className="text-center">
          <p className="mb-2 text-destructive">Error loading categories</p>
          <p className="text-sm text-muted-foreground">{getErrorMessage(error)}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-2">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Badge variant="outline" className="w-fit rounded-md border-blue-200 bg-blue-50 text-blue-700">
            Category administration
          </Badge>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {categoryKind === 'project' ? 'Project categories' : 'Job categories'}
            </h1>
            <Badge variant="secondary" className="rounded-md capitalize">{categoryKind}</Badge>
            <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground">
              <FolderTree className="mr-1 inline h-3.5 w-3.5" />
              {categories.length} total
            </span>
            {search.trim() ? (
              <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground">
                {filteredCategories.length} matching
              </span>
            ) : null}
          </div>
        </div>

        <Button className="rounded-lg" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create category
        </Button>
      </section>

      <Card className="overflow-hidden rounded-[28px] border-white/70 bg-card/90 shadow-[0_20px_40px_-35px_rgba(15,23,42,0.55)]">
        <CardHeader className="space-y-0 border-b border-border/60 pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
            <div className="relative min-w-[240px] lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, or image"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 pl-9"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="rounded-lg border border-border/70 p-3">
                  <Skeleton className="h-3.5 w-48" />
                  <Skeleton className="mt-2 h-3.5 w-full" />
                </div>
              ))}
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="py-16">
              <Empty>
                <div className="text-center">
                  <FolderTree className="mx-auto mb-3 h-8 w-8 text-muted-foreground/70" />
                  <p className="text-lg font-medium">No categories found</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {search.trim() ? 'Adjust your search or create a new category.' : 'Create a category to get started.'}
                  </p>
                  <Button className="mt-4 rounded-lg" onClick={() => setCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create category
                  </Button>
                </div>
              </Empty>
            </div>
          ) : (
            <div className={`overflow-x-auto ${commerceTableClassName}`}>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-16 pl-4">Logo</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Image</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="pr-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCategories.map((category) => {
                    const isSelected = selectedCategory?.id === category.id
                    const isActive = category.active !== false

                    return (
                      <TableRow
                        key={category.id}
                        className={cn(
                          'cursor-pointer',
                          !isActive && 'opacity-70',
                          isSelected && 'bg-primary/5 hover:bg-primary/5',
                        )}
                        onClick={() => setSelectedCategory(category)}
                      >
                        <TableCell className="pl-4">
                          <CategoryLogo logoUrl={category.logo_url} name={category.name} />
                        </TableCell>
                        <TableCell className="font-medium text-foreground">{category.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-[11px] font-normal">
                            {category.slug}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={isActive
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-slate-50 text-slate-600'}
                          >
                            {isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {category.logo_url ? 'Uploaded' : '-'}
                        </TableCell>
                        <TableCell>
                          <CompactTableDate value={category.created_at} />
                        </TableCell>
                        <TableCell className="pr-4 text-right" data-row-action="true">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(event) => event.stopPropagation()}>
                              <Button variant="ghost" size="icon" aria-label={`Actions for ${category.name}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44" onClick={(event) => event.stopPropagation()}>
                              <DropdownMenuItem onClick={() => openEditDialog(category)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => toggleCategoryActive(category)}
                                disabled={deactivateMutation.isPending || activateMutation.isPending}
                              >
                                {isActive ? (
                                  <>
                                    <PowerOff className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <Power className="mr-2 h-4 w-4" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteCategory(category)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open)
        if (!open) {
          resetCreateForm()
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create category</DialogTitle>
            <DialogDescription>
              Add a new category for the active service branch.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="Cleaning" {...register('name')} aria-invalid={!!errors.name} />
              {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="slug">Slug</Label>
                <span className="text-xs text-muted-foreground">
                  Auto: {slugify(nameValue || 'category-name') || 'category-name'}
                </span>
              </div>
              <Input id="slug" placeholder="cleaning" {...register('slug')} aria-invalid={!!errors.slug} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_file">Category image</Label>
              <div className="flex items-center gap-3">
                <CategoryLogo logoUrl={createLogoPreview} name={nameValue || 'Category'} />
                <Input
                  ref={createImageInputRef}
                  id="logo_file"
                  type="file"
                  accept="image/*"
                  onChange={(event) => setCreateImage(event.target.files?.[0])}
                />
              </div>
              {createLogoPreview ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => setCreateImage()}>
                  Remove image
                </Button>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create category'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editCategory)} onOpenChange={(open) => {
        if (!open) {
          setEditCategory(null)
          resetEditForm()
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit category</DialogTitle>
            <DialogDescription>
              Update category details for the active service branch.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit(onEditSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" placeholder="Cleaning" {...registerEdit('name')} aria-invalid={!!editErrors.name} />
              {editErrors.name ? <p className="text-sm text-destructive">{editErrors.name.message}</p> : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="edit-slug">Slug</Label>
                <span className="text-xs text-muted-foreground">
                  Auto: {slugify(editNameValue || 'category-name') || 'category-name'}
                </span>
              </div>
              <Input id="edit-slug" placeholder="cleaning" {...registerEdit('slug')} aria-invalid={!!editErrors.slug} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-logo_file">Category image</Label>
              <div className="flex items-center gap-3">
                <CategoryLogo logoUrl={editLogoPreview} name={editNameValue || editCategory?.name || 'Category'} />
                <Input
                  ref={editImageInputRef}
                  id="edit-logo_file"
                  type="file"
                  accept="image/*"
                  onChange={(event) => setEditImage(event.target.files?.[0])}
                />
              </div>
              {editLogoPreview ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditLogoFile(null)
                    setEditLogoPreview('')
                    setEditLogoRemoved(true)
                    if (editImageInputRef.current) editImageInputRef.current.value = ''
                  }}
                >
                  Remove image
                </Button>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => {
                setEditCategory(null)
                resetEditForm()
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedCategory)} onOpenChange={(open) => {
        if (!open) {
          setSelectedCategory(null)
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          {selectedCategory ? (
            <>
              <DialogHeader>
                <DialogTitle>{selectedCategory.name}</DialogTitle>
                <DialogDescription>Category details for the active service branch.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center gap-3 rounded-2xl border border-border/70 bg-muted/20 p-4">
                  <CategoryLogo logoUrl={selectedCategory.logo_url} name={selectedCategory.name} />
                  <div>
                    <p className="font-medium text-foreground">{selectedCategory.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedCategory.slug}</p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Status</p>
                    <p className="mt-1 text-sm text-foreground">
                      {selectedCategory.active === false ? 'Inactive' : 'Active'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Created</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(selectedCategory.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Image</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {selectedCategory.logo_url ? 'Image uploaded for this category.' : 'No image uploaded.'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(selectedCategory)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleCategoryActive(selectedCategory)}
                    disabled={deactivateMutation.isPending || activateMutation.isPending}
                  >
                    {selectedCategory.active === false ? (
                      <>
                        <Power className="mr-2 h-4 w-4" />
                        Activate
                      </>
                    ) : (
                      <>
                        <PowerOff className="mr-2 h-4 w-4" />
                        Deactivate
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteCategory(selectedCategory)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteCategory)} onOpenChange={(open) => {
        if (!open && !deleteMutation.isPending) {
          setDeleteCategory(null)
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium text-foreground">{deleteCategory?.name}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending || !deleteCategory}
              onClick={(event) => {
                event.preventDefault()
                if (deleteCategory) {
                  deleteMutation.mutate(String(deleteCategory.id))
                }
              }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
